import { motion } from "framer-motion";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMemo } from "react";
import { FuelMenuNav } from "@/components/fuel-activity/FuelMenuNav";
import { FuelYearTabs } from "@/components/fuel-activity/FuelYearPicker";
import { YearlySpendBreakdownView } from "@/components/fuel-activity/YearlySpendBreakdownView";
import { PageHeader } from "@/components/ui/PageHeader";
import { ExplorerSkeleton } from "@/components/ui/PageSkeleton";
import { SafeImage } from "@/components/ui/SafeImage";
import { getAvailableFuelYears, getYearlySpendBreakdown } from "@/lib/fuel-analytics";
import { useUserFuelActivity } from "@/lib/queries";
import { imageSrc, userDisplayName } from "@/lib/utils";

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

export function UserFuelSpendYearPage() {
  const { userId = "", year: yearParam } = useParams();
  const year = Number(yearParam);
  const { data, isLoading, error } = useUserFuelActivity(userId);

  const user = data?.user ?? null;
  const name = user ? userDisplayName(user) : userId.slice(0, 8);
  const profilePhoto = user ? imageSrc(user.profilePhoto) : null;

  const availableYears = useMemo(() => (data ? getAvailableFuelYears(data.logs) : []), [data]);

  const breakdown = useMemo(() => {
    if (!data || !Number.isFinite(year)) return null;
    const userList = user ? [user] : [];
    return getYearlySpendBreakdown(data.logs, userList, data.cars, data.stations, year);
  }, [data, user, year]);

  if (!Number.isFinite(year)) {
    return <Navigate to={`/fuel-activity/users/${userId}/spend`} replace />;
  }

  if (!isLoading && data && availableYears.length > 0 && !availableYears.includes(year)) {
    return <Navigate to={`/fuel-activity/users/${userId}/spend/${availableYears[0]}`} replace />;
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="space-y-6"
    >
      <Link
        to={`/fuel-activity/users/${userId}/spend`}
        className="inline-flex text-sm font-semibold text-muted transition hover:text-foreground"
      >
        ← All years
      </Link>

      <PageHeader
        title={`${name} · ${year}`}
        description={
          breakdown?.isCurrentYear
            ? "Year-to-date fuel spend breakdown for this user."
            : `Full-year fuel spend breakdown for ${year}.`
        }
        showGreeting={false}
        action={
          profilePhoto ? (
            <SafeImage
              src={profilePhoto}
              alt={name}
              width={48}
              height={48}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-subtle text-sm font-bold text-accent-foreground">
              {name.charAt(0).toUpperCase()}
            </div>
          )
        }
      />

      <FuelMenuNav />

      {availableYears.length > 0 ? (
        <FuelYearTabs
          years={availableYears}
          activeYear={year}
          basePath={`/fuel-activity/users/${userId}/spend`}
        />
      ) : null}

      {isLoading ? (
        <ExplorerSkeleton title={`${year} spend`} />
      ) : error || !data || !breakdown ? (
        <p className="text-sm text-red-600">Failed to load spend breakdown for {year}.</p>
      ) : (
        <YearlySpendBreakdownView
          breakdown={breakdown}
          scope="user"
          basePath={`/fuel-activity/users/${userId}/spend`}
        />
      )}
    </motion.div>
  );
}
